import { datadogLogs } from "@datadog/browser-logs";
import { Provider as RollbarProvider } from "@rollbar/react";
import cuid from "cuid";
import { some } from "lodash";
import React, { useEffect } from "react";

import {
  COMMIT,
  DATADOG_CLIENT_TOKEN,
  DATADOG_ENABLED,
  ENVIRONMENT,
  ROLLBAR_ACCESS_TOKEN,
  ROLLBAR_ENABLED,
} from "config";

export function checkIgnoreDatadogErrors(
  isUncaught: boolean,
  args: unknown,
  payload?: unknown,
): boolean {
  // Filter out Datadog SDK traces/errors
  const datadogPatterns = ["@datadog/browser", "handlingStack", "node_modules/@datadog"];

  const checkPatterns = (obj: unknown): boolean => {
    if (!obj) return false;

    // Handle Error objects - check message and stack
    if (obj instanceof Error) {
      const { message, stack } = obj;
      const messageStr = `${message || ""}${stack || ""}`.toLowerCase();
      return datadogPatterns.some((pattern) => messageStr.includes(pattern.toLowerCase()));
    }

    // Handle data objects
    if (typeof obj === "object") {
      const objString = JSON.stringify(obj).toLowerCase();
      if (datadogPatterns.some((pattern) => objString.includes(pattern.toLowerCase()))) {
        return true;
      }

      // Check nested trace frames
      const data = obj as any;
      if (data.trace_chain) {
        for (const trace of data.trace_chain) {
          if (trace.frames) {
            for (const frame of trace.frames) {
              if (
                frame.filename &&
                datadogPatterns.some((pattern) =>
                  frame.filename.toLowerCase().includes(pattern.toLowerCase()),
                )
              ) {
                return true;
              }
            }
          }
        }
      }
    }

    return false;
  };

  // Check both args and the full payload
  return checkPatterns(args) || checkPatterns(payload);
}

function LoggingWrapper({
  userId,
  classId,
  children,
}: {
  children: React.ReactNode;
  userId: string;
  classId: string;
}) {
  const rollbarConfig = {
    accessToken: ROLLBAR_ACCESS_TOKEN,
    captureUncaught: true,
    captureUnhandledRejections: true,
    enabled: ROLLBAR_ENABLED,
    checkIgnore: checkIgnoreDatadogErrors,
    payload: {
      environment: ENVIRONMENT,
      sha: COMMIT,
      client: {
        javascript: {
          code_version: COMMIT,
          source_map_enabled: true,
        },
      },
    },
  };

  useEffect(() => {
    if (DATADOG_ENABLED && classId && userId) {
      datadogLogs.init({
        clientToken: DATADOG_CLIENT_TOKEN || "",
        service: "flight",
        env: ENVIRONMENT,
        version: COMMIT,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: "all",
        sessionSampleRate: 100,
        beforeSend: (log) => {
          // Discard specific logs
          return !some(["[PubsubWebSocket]"], (str) => log.message.includes(str));
        },
      });
      datadogLogs.setGlobalContextProperty("classId", classId);
      datadogLogs.setGlobalContextProperty("userId", userId);
      datadogLogs.setGlobalContextProperty("session_id", cuid());
      datadogLogs.setGlobalContextProperty("view", {
        referrer: document.referrer,
        url: window.location.href,
      });
    }
  }, [classId, userId]);

  return <RollbarProvider config={rollbarConfig}>{children}</RollbarProvider>;
}

export { LoggingWrapper };
